/**
 * Where you were in a paper — the section you were reading — for Home's
 * Continue: "Documents · you were in §4 Results · 20 min ago" (navigation
 * handoff §3). Continue opens the paper back at that section.
 *
 * Mounted beside the editor rather than inside it: it reads the page, it
 * doesn't need the editor's state. The section is the last heading above the
 * top of the view. Kept in localStorage per paper; failing storage only means
 * Continue opens at the top.
 */
import { useEffect } from "react";
import { useLocation } from "react-router";

const KEY = "lilia.nav.place.v1";
const HEADINGS = ".ProseMirror h1, .ProseMirror h2, .ProseMirror h3, .ProseMirror h4";

export interface Place { heading: string; at: number }

export function placeIn(docId: string): Place | null {
  try {
    return (JSON.parse(window.localStorage.getItem(KEY) ?? "{}") as Record<string, Place>)[docId] ?? null;
  } catch {
    return null;
  }
}

function savePlace(docId: string, heading: string | null) {
  try {
    const all = JSON.parse(window.localStorage.getItem(KEY) ?? "{}") as Record<string, Place>;
    if (heading) all[docId] = { heading, at: Date.now() };
    else delete all[docId];
    const ids = Object.keys(all);
    if (ids.length > 100) {
      ids.sort((a, b) => all[a].at - all[b].at).slice(0, ids.length - 100).forEach((k) => delete all[k]);
    }
    window.localStorage.setItem(KEY, JSON.stringify(all));
  } catch {
    /* forgetting is the failure mode */
  }
}

const text = (el: Element) => (el.textContent ?? "").replace(/\s+/g, " ").trim();

/** Navigation state asking the editor to open at the saved section. */
export const RESUME_STATE = { resume: true } as const;

export function RememberPlace({ docId }: { docId: string }) {
  const location = useLocation();
  const resume = (location.state as { resume?: boolean } | null)?.resume === true;

  // Opening back at the section, once the paper has rendered it.
  useEffect(() => {
    if (!resume) return;
    const place = placeIn(docId);
    if (!place) return;
    let stopped = false;
    const started = Date.now();
    const tick = () => {
      if (stopped || Date.now() - started > 8000) return;
      const el = [...document.querySelectorAll(HEADINGS)].find((h) => text(h) === place.heading);
      if (el) { el.scrollIntoView({ block: "start" }); return; }
      window.setTimeout(tick, 150);
    };
    tick();
    return () => { stopped = true; };
  }, [docId, resume]);

  // Following the reading: the last heading above the top of the view.
  useEffect(() => {
    let timer = 0;
    const note = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        const heads = [...document.querySelectorAll(HEADINGS)];
        if (heads.length === 0) return;
        let current: Element | null = null;
        for (const h of heads) {
          if (h.getBoundingClientRect().top <= 140) current = h;
          else break;
        }
        savePlace(docId, current ? text(current) || null : null);
      }, 400);
    };
    document.addEventListener("scroll", note, true);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("scroll", note, true);
    };
  }, [docId]);

  return null;
}
